import { useEffect, useState, type ReactNode } from "react";
import { RouterProvider } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Providers } from "@/app/providers";
import { router } from "@/app/router";
import { useAuthStore } from "@/features/auth/stores/authStore";

/**
 * Renova o access token (só-em-memória, OWASP A02) ANTES de renderizar as rotas quando há um
 * `user` persistido sem token — caso de reload ou retorno de redirect externo (ex.: checkout do
 * Asaas). Evita o 401 visível da 1ª chamada protegida (que o interceptor recuperaria mesmo assim).
 */
function AuthGate({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasToken = useAuthStore((s) => s.accessToken !== null);
  const refresh = useAuthStore((s) => s.refresh);

  useEffect(() => {
    let active = true;
    if (isAuthenticated && !hasToken) {
      void refresh().finally(() => active && setReady(true));
    } else {
      setReady(true);
    }
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap roda uma vez no mount
  }, []);

  if (!ready) {
    return (
      <div className="grid min-h-screen place-items-center bg-bg" role="status" aria-busy="true">
        <Loader2 className="animate-spin text-brand-500" size={28} />
      </div>
    );
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Providers>
      <AuthGate>
        <RouterProvider router={router} />
      </AuthGate>
    </Providers>
  );
}
